/**
 * Apex Puzzle — Boiler-Bound Sundew.
 *
 * Implements the multi-phase Sundew fight described in
 * `docs/specs/SPEC-conservatory-rebuild.md` §5.1–5.3 and the 2026-04-14
 * Companion Book pages 20-21. Composition mirrors the L3 apex
 * `TheOrrery.ts`: a single `phase` tracker, cross-phase event handlers
 * wired in `setup`, and per-phase `tick*` helpers invoked from a
 * single switch inside `update`.
 *
 * Phase machine:
 *   idle   — waits for the nectar escort (puzzle id `nectarEscort`) to
 *            report solved via `ctx.levelState` or a late `PUZZLE_SOLVED`
 *            event. Neither branch runs until the escort is done.
 *   phase1 — 3 sundew-variant ValveCranks. Rolling 30-second window to
 *            activate all three. Heat pulse every 5s.
 *   phase2 — 4 CorruptedNodes (canonical §5.2). Tongue sweeps every 6s
 *            (incubation only). Aura Sight reveals nodes through the
 *            mist; TTT hits clear them at 4 hits each.
 *   phase3 — "Iron jaws" synchronized dash. Both cats enter a trigger
 *            zone within a window (500ms, 1500ms under Chronos Bubble).
 *            On arming, the jaws publish a visual/audio framing event so
 *            UI, audio, and VFX systems can stage the risk moment the
 *            book describes as a live predator-mouth.
 *   phase3b — Internal Purr hold. Minna must hold `F` inside the Sundew's
 *            internal chamber for 6 seconds continuously to "unwire" it
 *            from the inside (book p.20-21, SPEC §5.3 open question 4).
 *            Break on release, leaving the chamber, or Minna's Purr
 *            deactivating. Progress ticks fire on SUNDEW_INTERNAL_PURR_PROGRESS
 *            so the HUD + particles can show the 0→1 ramp.
 *   solved — terminal. `PUZZLE_SOLVED` has been emitted.
 */
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3, Color4 } from "@babylonjs/core/Maths/math.color";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { PointLight } from "@babylonjs/core/Lights/pointLight";
import { ParticleSystem } from "@babylonjs/core/Particles/particleSystem";
// Babylon v9 loads particle shaders lazily when a ParticleSystem starts.
// Register them eagerly so NullEngine test teardown cannot race the async load.
import "@babylonjs/core/Shaders/particles.vertex";
import "@babylonjs/core/Shaders/particles.fragment";

import { createSoftParticleTexture } from "../../../core/particleTextures";

import { ValveCrank } from "../../../entities/interactives/ValveCrank";
import { CorruptedNode } from "../../../entities/interactives/CorruptedNode";

import type { ChronosBubble } from "../../../abilities/coop/ChronosBubble";
import type { ConservatoryCycle } from "../../../systems/ConservatoryCycle";

import { GameEvents } from "../../../config/events";
import { COLORS } from "../../../config/GameConfig";
import {
  getAssetManager,
  InstantiatedModel,
} from "../../../systems/AssetManager";
import { tagAs } from "../../PlaceholderTag";

import type {
  PuzzleContext,
  PuzzleCue,
  PuzzleModule,
  PuzzleState,
} from "../../PuzzleModule";
import type { CueDef } from "../../../ui/tutorialCues";
import type { PuzzleQAHints } from "../../qa/PuzzleQAHints";

type SundewPhase =
  | "idle"
  | "phase1"
  | "phase2"
  | "phase3"
  | "phase3b"
  | "solved";

interface ValveDef {
  key: string;
  pos: Vector3;
}

interface NodeDef {
  key: string;
  pos: Vector3;
}

const SUNDEW_MODEL_PATH =
  "/assets/models/environment/conservatory_boiler_sundew.glb";

const SUNDEW_ROOT = new Vector3(133, 2.4, -2.8);

const PHASE_PROGRESS: Record<SundewPhase, number> = {
  idle: 0,
  phase1: 0.2,
  phase2: 0.45,
  phase3: 0.7,
  phase3b: 0.85,
  solved: 1,
};

const BOILER_CORE = new Vector3(133, 3.5, 0);

const PHASE1_VALVES: ValveDef[] = [
  { key: "sundew_valve_0", pos: new Vector3(131, 4, 0) },
  { key: "sundew_valve_1", pos: new Vector3(135, 4, 0) },
  { key: "sundew_valve_2", pos: new Vector3(133, 7, 0) },
];

const PHASE2_NODES: NodeDef[] = [
  { key: "sundew_node_0", pos: new Vector3(131, 3.5, 0) },
  { key: "sundew_node_1", pos: new Vector3(135, 3.5, 0) },
  { key: "sundew_node_2", pos: new Vector3(133, 6, 0) },
  { key: "sundew_node_3", pos: new Vector3(133, 1, 0) },
];

/** Phase 1 heat pulse cadence. */
const HEAT_PULSE_PERIOD_S = 5;
const HEAT_PULSE_RADIUS = 6;

/** Phase 1 rolling-window duration for all-valves activation. */
const PHASE1_WINDOW_S = 30;

/** Phase 2 tongue sweep cadence. */
const TONGUE_SWEEP_PERIOD_S = 6;
const TONGUE_SWEEP_FROM_X = 131;
const TONGUE_SWEEP_TO_X = 135;
const TONGUE_SWEEP_DURATION_MS = 1400;

/** Phase 3 trigger zone. */
const DASH_ZONE_CENTER = new Vector3(133, 3, -1);
const DASH_ZONE_RADIUS = 1.5;

/** Phase 3 synchronization windows. */
const DASH_WINDOW_MS_NORMAL = 500;
const DASH_WINDOW_MS_CHRONOS = 1500;

/** Basil horizontal reach for routing TTT hits to a boiler valve or node. */
const TTT_TARGET_HORIZONTAL_RADIUS = 2.4;
/** Vertical slack for boiler-mounted TTT targets in the 2.5D play plane. */
const TTT_TARGET_VERTICAL_RADIUS = 6.6;

/**
 * Phase 3B internal chamber — the "inside" of the Sundew mouth where
 * Minna holds the Purr to unwire the creature. Sits one unit deeper than
 * the dash zone along -Z so it reads as "entering" the maw. Radius is
 * slightly tighter than the dash zone because the chamber is a pocket,
 * not an approach arc.
 */
const INTERNAL_CHAMBER_CENTER = new Vector3(133, 3, -2.2);
const INTERNAL_CHAMBER_RADIUS = 1.2;

/**
 * Canonical Phase 3B hold duration — book p.20-21 describes Minna
 * "unwiring from the inside," SPEC §5.3 open question 4 pins the
 * recommended duration at 6 seconds.
 */
const INTERNAL_PURR_HOLD_MS = 6000;

const CUE = {
  grip: { cat: "basil", parts: [{ action: "turnStart" }, { text: "grip a valve" }] },
  gripNode: { cat: "basil", parts: [{ action: "turnStart" }, { text: "grip a node" }] },
  spin: { cat: "basil", parts: [{ action: "rhythmHit" }, { text: "on the beat" }] },
  reveal: { cat: "minna", parts: [{ action: "aura" }, { text: "reveal the nodes" }] },
  dash: { cat: "both", parts: [{ text: "into the maw together" }] },
  purr: { cat: "minna", parts: [{ action: "purr", hold: true }, { text: "unwire it" }] },
} satisfies Record<string, CueDef>;
const OBJECTIVE = "Unwire the Boiler-Bound Sundew";
const above = (v: Vector3, dy = 1.2) => ({ x: v.x, y: v.y + dy, z: 0 });

export function isInsideSundewTurnTableReach(
  basilPos: { x: number; y: number },
  targetPos: { x: number; y: number },
): boolean {
  return (
    Math.abs(basilPos.x - targetPos.x) <= TTT_TARGET_HORIZONTAL_RADIUS &&
    Math.abs(basilPos.y - targetPos.y) <= TTT_TARGET_VERTICAL_RADIUS
  );
}

export function isInsideSundewDashZone(pos: { x: number; y: number }): boolean {
  const dx = pos.x - DASH_ZONE_CENTER.x;
  const dy = pos.y - DASH_ZONE_CENTER.y;
  return dx * dx + dy * dy <= DASH_ZONE_RADIUS * DASH_ZONE_RADIUS;
}

export function isInsideSundewInternalChamber(pos: { x: number; y: number }): boolean {
  const dx = pos.x - INTERNAL_CHAMBER_CENTER.x;
  const dy = pos.y - INTERNAL_CHAMBER_CENTER.y;
  return dx * dx + dy * dy <= INTERNAL_CHAMBER_RADIUS * INTERNAL_CHAMBER_RADIUS;
}

export class BoilerBoundSundewPuzzle implements PuzzleModule {
  readonly id = "sundewApex";
  readonly displayName = "Boiler-Bound Sundew";

  private ctx!: PuzzleContext;
  private readonly conservatoryCycle: ConservatoryCycle;
  private readonly chronosBubble: ChronosBubble;

  private phase: SundewPhase = "idle";

  private sundewProp: InstantiatedModel | null = null;
  private boilerCoreMesh: Mesh | null = null;
  private boilerCoreMat: StandardMaterial | null = null;
  private boilerCoreLight: PointLight | null = null;

  private phase1Valves: ValveCrank[] = [];
  private phase2Nodes: CorruptedNode[] = [];

  private readonly activeValves = new Set<string>();
  /** Per-valve activation timestamp (ms) used for the rolling window. */
  private readonly valveActivationTimes = new Map<string, number>();

  private heatPulseAccum = 0;
  private tongueSweepAccum = 0;
  /** Absolute time (ms) the current sweep ends, or 0 if no sweep is live. */
  private tongueSweepEndsAtMs = 0;
  /** Set of cats already damaged by the current sweep so we don't re-damage. */
  private readonly tongueDamagedThisSweep = new Set<"minna" | "basil">();

  private nodesRemaining = PHASE2_NODES.length;

  private minnaEnteredMs: number | null = null;
  private basilEnteredMs: number | null = null;
  private minnaInZoneLast = false;
  private basilInZoneLast = false;
  private lastDesyncToastMs = -Infinity;

  /** Scene-time accumulator in ms, tracked off the elapsed value. */
  private elapsedMs = 0;

  /**
   * Phase 3B "internal Purr hold" state (canon: unwiring from the inside).
   * `heldMs` is the continuous in-chamber + Purr-active hold accrued so
   * far. Reset to 0 whenever Minna leaves the chamber or releases `F`.
   */
  private internalPurrHeldMs = 0;
  /** Whether Minna was inside the chamber with Purr active last frame. */
  private internalPurrHeldLast = false;
  /** Last emitted progress fraction (to avoid spamming 0-value ticks). */
  private internalPurrLastEmitted = -1;
  /** Active Turn-Table target selected via the `1`/`2` interaction keys. */
  private turnTableTargetKey: string | null = null;

  /**
   * "Iron jaws" framing VFX — procedural particle + ring set spawned when
   * Phase 3 arms the dash zone. Stored so we can dispose deterministically
   * on phase advance or puzzle teardown.
   */
  private ironJawsRing: Mesh | null = null;
  private ironJawsRingMat: StandardMaterial | null = null;
  private ironJawsParticles: ParticleSystem | null = null;
  private ironJawsArmed = false;

  constructor(
    conservatoryCycle: ConservatoryCycle,
    chronosBubble: ChronosBubble,
  ) {
    this.conservatoryCycle = conservatoryCycle;
    this.chronosBubble = chronosBubble;
  }

  async setup(ctx: PuzzleContext): Promise<void> {
    this.ctx = ctx;

    this.buildBoilerCore();

    await this.loadSundewProp();

    this.buildPhase1Valves();
    this.buildPhase2Nodes();

    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.PUZZLE_SOLVED, (payload) => {
        if (payload.puzzleId === "nectarEscort") {
          this.enterPhase1();
        }
      }),
    );

    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.SUNDEW_VALVE_ACTIVATED, ({ key }) => {
        if (this.phase !== "phase1") return;
        this.onValveActivated(key);
      }),
    );

    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.SUNDEW_NODE_CLEARED, () => {
        if (this.phase !== "phase2") return;
        this.nodesRemaining = Math.max(0, this.nodesRemaining - 1);
        if (this.nodesRemaining === 0) {
          this.enterPhase3();
        }
      }),
    );

    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.TURN_TABLE_TOUCH_HIT, () => {
        this.onTurnTableHit();
      }),
    );

    if (ctx.levelState.get("nectarEscort")?.solved === true) {
      this.enterPhase1();
    }

    this.registerMCPHook();
  }

  update(dt: number, elapsed: number): void {
    this.elapsedMs = elapsed * 1000;
    this.updateTurnTableTarget();

    switch (this.phase) {
      case "idle":
        this.tickIdle();
        return;
      case "phase1":
        this.tickPhase1(dt);
        return;
      case "phase2":
        this.tickPhase2(dt);
        return;
      case "phase3":
        this.tickPhase3();
        return;
      case "phase3b":
        this.tickPhase3b(dt);
        return;
      case "solved":
        return;
    }
  }

  getState(): PuzzleState {
    return {
      solved: this.phase === "solved",
      progress: PHASE_PROGRESS[this.phase],
    };
  }

  getCue(): PuzzleCue | null {
    const basil = this.ctx.basil.mesh.position;
    const nearest = <T extends { rootMesh: { position: Vector3 } }>(list: T[], open: (t: T) => boolean) =>
      list
        .filter(open)
        .reduce<T | null>((best, t) => (!best || Vector3.Distance(basil, t.rootMesh.position) < Vector3.Distance(basil, best.rootMesh.position) ? t : best), null);
    const targetOf = (list: Array<{ key: string; rootMesh: { position: Vector3 } }>) =>
      list.find((t) => t.key === this.turnTableTargetKey);
    switch (this.phase) {
      case "phase1": {
        const active = this.ctx.turnTableTouch.isActive ? targetOf(this.phase1Valves) : undefined;
        if (active) return { objective: OBJECTIVE, cue: CUE.spin, anchor: above(active.rootMesh.position) };
        const next = nearest(this.phase1Valves, (v) => !v.isActivated);
        return { objective: OBJECTIVE, cue: CUE.grip, anchor: next ? above(next.rootMesh.position) : above(BOILER_CORE) };
      }
      case "phase2": {
        if (!this.ctx.auraSight.isActive) return { objective: OBJECTIVE, cue: CUE.reveal, anchor: above(BOILER_CORE, 3) };
        const active = this.ctx.turnTableTouch.isActive ? targetOf(this.phase2Nodes) : undefined;
        if (active) return { objective: OBJECTIVE, cue: CUE.spin, anchor: above(active.rootMesh.position) };
        const next = nearest(this.phase2Nodes, (n) => !n.isCleared);
        return { objective: OBJECTIVE, cue: CUE.gripNode, anchor: next ? above(next.rootMesh.position) : above(BOILER_CORE) };
      }
      case "phase3":
        return { objective: OBJECTIVE, cue: CUE.dash, anchor: above(DASH_ZONE_CENTER, 1.6) };
      case "phase3b":
        return { objective: OBJECTIVE, cue: CUE.purr, anchor: above(INTERNAL_CHAMBER_CENTER, 1.6) };
      default:
        return null;
    }
  }

  onAbilityKey(key: string, pressed: boolean, _released: boolean): boolean {
    if (!pressed) return false;

    if (key === "1" && (this.phase === "phase1" || this.phase === "phase2")) {
      const target = this.pickNearestTurnTableTarget(this.ctx.basil.mesh.position);
      if (!target) {
        // Nothing of ours in reach: let the key through (e.g. to the
        // regulator's mid-escort cranks nearby) instead of swallowing it.
        this.deactivateTurnTableTarget();
        return false;
      }

      if (this.turnTableTargetKey === target.key && this.ctx.turnTableTouch.isActive) {
        this.deactivateTurnTableTarget();
        return true;
      }

      if (this.turnTableTargetKey) {
        this.ctx.basilAbilities.deactivateAbility("turnTableTouch");
      }
      // Route through the holder so ABILITY_ACTIVATED fires for
      // HUD/telemetry/banter. This site already checked the return value,
      // so semantics are preserved exactly (a refused activation — e.g.
      // same-press valve switch inside the 0.2s cooldown — now emits
      // ABILITY_BLOCKED instead of failing silently).
      this.ctx.turnTableTouch.setTarget(target.position, (_combo) => {});
      if (this.ctx.basilAbilities.activateAbility("turnTableTouch")) {
        this.turnTableTargetKey = target.key;
      }
      return true;
    }

    if (key === "2" && this.turnTableTargetKey) {
      this.ctx.turnTableTouch.attemptHit();
      return true;
    }

    return false;
  }

  getQAHints(): PuzzleQAHints {
    return {
      puzzleId: this.id,
      successEvent: GameEvents.PUZZLE_SOLVED,
      targets: [
        { id: "sundew_root",   position: { x: 133, y: 2.4 }, radius: 1.8, allowedCat: "either", label: "Sundew root (apex entrance)" },
        { id: "sundew_valve_0", meshName: "sundew_valve_0", position: { x: 131, y: 4 }, radius: 1.2, allowedCat: "basil", label: "Sundew valve 0" },
        { id: "sundew_valve_1", meshName: "sundew_valve_1", position: { x: 135, y: 4 }, radius: 1.2, allowedCat: "basil", label: "Sundew valve 1" },
        { id: "sundew_valve_2", meshName: "sundew_valve_2", position: { x: 133, y: 7 }, radius: 1.2, allowedCat: "basil", label: "Sundew valve 2 (high)" },
        { id: "sundew_node_0", meshName: "sundew_node_0", position: { x: 131, y: 3.5 }, radius: 1.0, allowedCat: "basil", label: "Corrupted node 0" },
        { id: "sundew_node_1", meshName: "sundew_node_1", position: { x: 135, y: 3.5 }, radius: 1.0, allowedCat: "basil", label: "Corrupted node 1" },
        { id: "sundew_node_2", meshName: "sundew_node_2", position: { x: 133, y: 6 },   radius: 1.0, allowedCat: "basil", label: "Corrupted node 2" },
        { id: "sundew_node_3", meshName: "sundew_node_3", position: { x: 133, y: 1 },   radius: 1.0, allowedCat: "basil", label: "Corrupted node 3" },
        { id: "dash_zone",      position: { x: 133, y: 3 }, radius: 1.5, allowedCat: "either", label: "Iron-jaws dash zone" },
        { id: "internal_chamber", position: { x: 133, y: 3 }, radius: 1.5, allowedCat: "minna", label: "Internal Purr chamber" },
      ],
      traversals: [
        // Phase 1 high-valve climb: basil OvLeap from sundew_root
        // (boiler ground level y=2.4) up to sundew_valve_2 at (133, 7).
        // Δy=4.6u — exceeds normal jump (peak rise 2.7u) so OvLeap
        // is required. Fire from rest, no horizontal drift (basil's
        // x already aligns with the valve).
        {
          id: "basil_ovleap_to_high_valve",
          kind: "ovLeap",
          cat: "basil",
          from: "sundew_root",
          to: "sundew_valve_2",
          successWindowMs: 5_000,
          recipe: [
            { key: "4", holdMs: 100 },
          ],
        },
        // Phase 3 iron-jaws dash: both cats simultaneously enter
        // the dash zone. Recipe holds an east walk-key for both
        // cats — assumes navigation has staged each just west of
        // the zone. The 500ms (or 1500ms under Chronos Bubble) window
        // is enforced by BoilerBoundSundew.update; this recipe
        // documents the expected input shape.
        {
          id: "iron_jaws_dash_sync",
          kind: "coop",
          cat: "either",
          from: "sundew_root",
          to: "dash_zone",
          successWindowMs: 2_000,
          recipe: [
            { keyDown: "d" },
            { waitMs: 400 },
            { keyUp: "d" },
          ],
        },
      ],
    };
  }

  /**
   * Sub-puzzle state seeder. Shape:
   *   {
   *     phase?: "phase1" | "phase2" | "phase3" | "phase3b",
   *           // advance the state machine to the requested phase
   *     solved?: boolean,  // jump straight to the terminal `solved` phase
   *   }
   *
   * Multi-phase apex — the most expensive beats to replay manually are
   * phase2 (4 corrupted nodes × 4 TTT hits each), phase3 (dash-sync
   * timing window), and phase3b (6-second internal Purr hold). Seeding
   * directly into `phase2`/`phase3`/`phase3b` lets each phase be tested
   * without playing the preceding ones.
   *
   * Each transition routes through the canonical `enter*` helpers so
   * they still emit SUNDEW_PHASE_ADVANCED / HUD toasts, matching real
   * play side effects.
   */
  seed(state: Record<string, unknown>): void {
    const target = state.solved === true ? "solved" : state.phase;
    if (
      target !== "phase1" &&
      target !== "phase2" &&
      target !== "phase3" &&
      target !== "phase3b" &&
      target !== "solved"
    ) {
      return;
    }

    // Phase transitions are guarded (`enterPhase2` only fires from phase1,
    // etc.), so walk up through them in order regardless of the current
    // phase. Each `enter*` is idempotent via its own guard.
    const order: SundewPhase[] = ["phase1", "phase2", "phase3", "phase3b", "solved"];
    const targetIdx = order.indexOf(target);
    for (let i = 0; i <= targetIdx; i++) {
      const next = order[i];
      if (this.phase === next) continue;
      switch (next) {
        case "phase1":
          this.enterPhase1();
          break;
        case "phase2":
          // enterPhase2 guards on `phase === "phase1"`; ensure we're there.
          if (this.phase === "idle") this.enterPhase1();
          this.enterPhase2();
          break;
        case "phase3":
          if (this.phase === "idle") this.enterPhase1();
          if (this.phase === "phase1") this.enterPhase2();
          this.enterPhase3();
          break;
        case "phase3b":
          if (this.phase === "idle") this.enterPhase1();
          if (this.phase === "phase1") this.enterPhase2();
          if (this.phase === "phase2") this.enterPhase3();
          this.enterPhase3b();
          break;
        case "solved":
          if (this.phase === "idle") this.enterPhase1();
          if (this.phase === "phase1") this.enterPhase2();
          if (this.phase === "phase2") this.enterPhase3();
          if (this.phase === "phase3") this.enterPhase3b();
          this.completeApex();
          break;
      }
    }
  }

  dispose(): void {
    this.deactivateTurnTableTarget();
    this.disarmIronJaws();
    if (this.sundewProp) {
      this.sundewProp.dispose();
      this.sundewProp = null;
    }
    if (this.boilerCoreLight) {
      this.boilerCoreLight.dispose();
      this.boilerCoreLight = null;
    }
    if (this.boilerCoreMesh) {
      this.boilerCoreMesh.dispose();
      this.boilerCoreMesh = null;
    }
    if (this.boilerCoreMat) {
      this.boilerCoreMat.dispose();
      this.boilerCoreMat = null;
    }
    for (const valve of this.phase1Valves) valve.dispose();
    for (const node of this.phase2Nodes) node.dispose();
    this.phase1Valves = [];
    this.phase2Nodes = [];
  }

  getInternals(): {
    id: string;
    displayName: string;
    phase: SundewPhase;
    phase1Valves: ValveCrank[];
    phase2Nodes: CorruptedNode[];
    minnaEnteredMs: number | null;
    basilEnteredMs: number | null;
    internalPurrHeldMs: number;
    internalPurrHoldRequiredMs: number;
    ironJawsArmed: boolean;
    conservatoryCycle: ConservatoryCycle;
    chronosBubble: ChronosBubble;
    ctx: PuzzleContext;
    puzzle: BoilerBoundSundewPuzzle;
    forceDashSync: (deltaMs?: number) => void;
    internalChamberCenter: { x: number; y: number; z: number };
    internalChamberRadius: number;
    dashZoneCenter: { x: number; y: number; z: number };
    dashZoneRadius: number;
    dashWindowMsNormal: number;
    dashWindowMsChronos: number;
  } {
    return {
      id: this.id,
      displayName: this.displayName,
      phase: this.phase,
      phase1Valves: this.phase1Valves,
      phase2Nodes: this.phase2Nodes,
      minnaEnteredMs: this.minnaEnteredMs,
      basilEnteredMs: this.basilEnteredMs,
      internalPurrHeldMs: this.internalPurrHeldMs,
      internalPurrHoldRequiredMs: INTERNAL_PURR_HOLD_MS,
      ironJawsArmed: this.ironJawsArmed,
      conservatoryCycle: this.conservatoryCycle,
      chronosBubble: this.chronosBubble,
      ctx: this.ctx,
      puzzle: this,
      // QA affordance: the physical dash zone sits above an un-floored
      // stretch of the conservatory so auto-respawn pulls live cats out
      // before they can enter it. Lets integration tests stamp both
      // dash times at once and run `resolveDashSync` without teleport
      // race conditions.
      forceDashSync: (deltaMs = 0) => {
        this.minnaEnteredMs = this.elapsedMs;
        this.basilEnteredMs = this.elapsedMs + deltaMs;
        this.resolveDashSync();
      },
      internalChamberCenter: {
        x: INTERNAL_CHAMBER_CENTER.x,
        y: INTERNAL_CHAMBER_CENTER.y,
        z: INTERNAL_CHAMBER_CENTER.z,
      },
      internalChamberRadius: INTERNAL_CHAMBER_RADIUS,
      dashZoneCenter: {
        x: DASH_ZONE_CENTER.x,
        y: DASH_ZONE_CENTER.y,
        z: DASH_ZONE_CENTER.z,
      },
      dashZoneRadius: DASH_ZONE_RADIUS,
      dashWindowMsNormal: DASH_WINDOW_MS_NORMAL,
      dashWindowMsChronos: DASH_WINDOW_MS_CHRONOS,
    };
  }

  // ─────────────────────────────────────────────────────────────────
  // Setup helpers
  // ─────────────────────────────────────────────────────────────────

  private async loadSundewProp(): Promise<void> {
    try {
      const instance = await getAssetManager().instantiateSharedModel(
        this.ctx.scene,
        SUNDEW_MODEL_PATH,
      );
      this.sundewProp = instance;
      for (const root of instance.rootNodes) {
        if ("position" in root && "scaling" in root) {
          (root as { position: Vector3; scaling: Vector3 }).position =
            SUNDEW_ROOT.clone();
        }
      }
      for (const mesh of instance.meshes) {
        mesh.isPickable = false;
        tagAs(mesh, "final");
      }
    } catch {
      // GLB missing is tolerated — procedural placeholders stand in.
    }
  }

  private buildBoilerCore(): void {
    const { scene } = this.ctx;
    this.boilerCoreMesh = MeshBuilder.CreateSphere(
      "sundew_boilerCore",
      { diameter: 1.1, segments: 16 },
      scene,
    );
    this.boilerCoreMesh.position = BOILER_CORE.clone();
    this.boilerCoreMat = new StandardMaterial(
      "sundew_boilerCoreMat",
      scene,
    );
    this.boilerCoreMat.diffuseColor = Color3.FromHexString("#1a0a0a");
    this.boilerCoreMat.emissiveColor = Color3.FromHexString("#d66b2a").scale(
      0.35,
    );
    this.boilerCoreMat.specularColor = Color3.Black();
    this.boilerCoreMat.alpha = 0.92;
    this.boilerCoreMesh.material = this.boilerCoreMat;
    tagAs(this.boilerCoreMesh, "placeholder");

    this.boilerCoreLight = new PointLight(
      "sundew_boilerCoreLight",
      BOILER_CORE.clone(),
      scene,
    );
    this.boilerCoreLight.diffuse = Color3.FromHexString("#d66b2a");
    this.boilerCoreLight.intensity = 0.55;
    this.boilerCoreLight.range = 8;
  }

  private buildPhase1Valves(): void {
    const { scene } = this.ctx;
    this.phase1Valves = PHASE1_VALVES.map(
      (def) =>
        new ValveCrank(scene, {
          key: def.key,
          position: def.pos,
          variant: "sundew",
          requiredHits: 3,
          label: def.key,
        }),
    );
    for (const valve of this.phase1Valves) {
      this.ctx.registerInteractive({
        mesh: valve.rootMesh,
        highlightTarget: valve.getHighlightTarget(),
        hidden: false,
        state: "revealed",
      });
    }
  }

  private buildPhase2Nodes(): void {
    const { scene } = this.ctx;
    this.phase2Nodes = PHASE2_NODES.map(
      (def) =>
        new CorruptedNode(scene, {
          key: def.key,
          position: def.pos,
          label: def.key,
        }),
    );
    for (const node of this.phase2Nodes) {
      this.ctx.registerInteractive({
        mesh: node.rootMesh,
        highlightTarget: node.getHighlightTarget(),
        hidden: false,
        state: "revealed",
      });
    }
  }

  private registerMCPHook(): void {
    const level = this.ctx.scene.metadata?.level as
      | {
          setPuzzle?: (puzzle: {
            getState: () => unknown;
            getInternals?: () => unknown;
          }) => void;
        }
      | undefined;
    level?.setPuzzle?.({
      getState: () => ({
        puzzleId: this.id,
        phase: this.phase,
        progress: PHASE_PROGRESS[this.phase],
        solved: this.phase === "solved",
        nodesRemaining: this.nodesRemaining,
        activeValves: Array.from(this.activeValves),
        internalPurrHeldMs: this.internalPurrHeldMs,
        internalPurrHoldRequiredMs: INTERNAL_PURR_HOLD_MS,
        ironJawsArmed: this.ironJawsArmed,
      }),
      getInternals: () => this.getInternals(),
    });
  }

  // ─────────────────────────────────────────────────────────────────
  // Per-phase ticks
  // ─────────────────────────────────────────────────────────────────

  private tickIdle(): void {
    if (this.ctx.levelState.get("nectarEscort")?.solved === true) {
      this.enterPhase1();
    }
  }

  private tickPhase1(dt: number): void {
    this.heatPulseAccum += dt;
    if (this.heatPulseAccum >= HEAT_PULSE_PERIOD_S) {
      this.heatPulseAccum -= HEAT_PULSE_PERIOD_S;
      this.ctx.gameEvents.emit(GameEvents.SUNDEW_HEAT_PULSE, {
        origin: { x: BOILER_CORE.x, y: BOILER_CORE.y, z: BOILER_CORE.z },
        radius: HEAT_PULSE_RADIUS,
      });
    }

    this.pruneExpiredValveActivations();
  }

  private tickPhase2(dt: number): void {
    const auraActive = this.ctx.auraSight.isActive;
    for (const node of this.phase2Nodes) {
      node.isAuraRevealed = auraActive;
      node.tickPulse(dt);
    }

    this.tongueSweepAccum += dt;
    if (this.tongueSweepAccum >= TONGUE_SWEEP_PERIOD_S) {
      this.tongueSweepAccum -= TONGUE_SWEEP_PERIOD_S;
      this.fireTongueSweep();
    }
    this.applyTongueSweepDamage();
  }

  private tickPhase3(): void {
    const { minna, basil } = this.ctx;
    const minnaInZone = this.isInDashZone(minna.mesh.position);
    const basilInZone = this.isInDashZone(basil.mesh.position);

    if (minnaInZone && !this.minnaInZoneLast) {
      this.minnaEnteredMs = this.elapsedMs;
      this.ctx.gameEvents.emit(GameEvents.SUNDEW_DASH_ENTERED, {
        cat: "minna",
        timeMs: this.elapsedMs,
      });
    }
    if (basilInZone && !this.basilInZoneLast) {
      this.basilEnteredMs = this.elapsedMs;
      this.ctx.gameEvents.emit(GameEvents.SUNDEW_DASH_ENTERED, {
        cat: "basil",
        timeMs: this.elapsedMs,
      });
    }
    this.minnaInZoneLast = minnaInZone;
    this.basilInZoneLast = basilInZone;

    if (this.minnaEnteredMs !== null && this.basilEnteredMs !== null) {
      this.resolveDashSync();
    }
  }

  /**
   * Phase 3B per-frame tick — advance the "internal Purr hold" timer
   * while Minna is inside the internal chamber AND holding Purr (`F`).
   * Break and reset on leaving the chamber or releasing the Purr. Emit
   * progress ticks so HUD + VFX can render the 0→1 ramp.
   */
  private tickPhase3b(dt: number): void {
    const { minna, calmingResonance } = this.ctx;
    const inChamber = this.isInInternalChamber(minna.mesh.position);
    const purring = calmingResonance.isActive;
    const holding = inChamber && purring;

    if (holding) {
      this.internalPurrHeldMs += dt * 1000;
      if (this.internalPurrHeldMs > INTERNAL_PURR_HOLD_MS) {
        this.internalPurrHeldMs = INTERNAL_PURR_HOLD_MS;
      }
    } else if (this.internalPurrHeldLast) {
      // Transitioned from holding → not holding. Reset the timer and emit
      // one final zeroed progress tick so the HUD can collapse the bar.
      this.internalPurrHeldMs = 0;
      this.emitInternalPurrProgress(0);
    }
    this.internalPurrHeldLast = holding;

    if (holding) {
      const progress = Math.min(
        1,
        this.internalPurrHeldMs / INTERNAL_PURR_HOLD_MS,
      );
      this.emitInternalPurrProgress(progress);
      if (progress >= 1) {
        // Emit the terminal tick first so listeners can land their VFX
        // at the 1.0 mark before completeApex swaps the phase to solved.
        this.emitInternalPurrProgress(1, true);
        this.completeApex();
      }
    }
  }

  private emitInternalPurrProgress(progress: number, force = false): void {
    // Throttle: only emit when progress meaningfully changes so the
    // event bus doesn't flood on every frame at the same fraction.
    const quantized = Math.round(progress * 100) / 100;
    if (!force && quantized === this.internalPurrLastEmitted) return;
    this.internalPurrLastEmitted = quantized;
    this.ctx.gameEvents.emit(GameEvents.SUNDEW_INTERNAL_PURR_PROGRESS, {
      progress: quantized,
      requiredMs: INTERNAL_PURR_HOLD_MS,
      heldMs: this.internalPurrHeldMs,
    });
  }

  // ─────────────────────────────────────────────────────────────────
  // Phase 1 helpers
  // ─────────────────────────────────────────────────────────────────

  private onValveActivated(key: string): void {
    if (this.activeValves.has(key)) return;
    this.activeValves.add(key);
    this.valveActivationTimes.set(key, this.elapsedMs);

    this.pruneExpiredValveActivations();

    if (this.activeValves.size >= PHASE1_VALVES.length) {
      this.enterPhase2();
    }
  }

  private pruneExpiredValveActivations(): void {
    const windowMs = PHASE1_WINDOW_S * 1000;
    const cutoff = this.elapsedMs - windowMs;
    for (const [key, stampMs] of this.valveActivationTimes) {
      if (stampMs < cutoff) {
        this.valveActivationTimes.delete(key);
        this.activeValves.delete(key);
        const valve = this.phase1Valves.find((v) => v.key === key);
        if (valve && valve.isActivated) valve.resetCycle();
      }
    }
  }

  // ─────────────────────────────────────────────────────────────────
  // Phase 2 helpers
  // ─────────────────────────────────────────────────────────────────

  private fireTongueSweep(): void {
    this.tongueDamagedThisSweep.clear();
    this.tongueSweepEndsAtMs = this.elapsedMs + TONGUE_SWEEP_DURATION_MS;
    this.ctx.gameEvents.emit(GameEvents.SUNDEW_TONGUE_SWEEP, {
      fromX: TONGUE_SWEEP_FROM_X,
      toX: TONGUE_SWEEP_TO_X,
      durationMs: TONGUE_SWEEP_DURATION_MS,
    });
  }

  private applyTongueSweepDamage(): void {
    if (this.tongueSweepEndsAtMs === 0) return;
    if (this.elapsedMs >= this.tongueSweepEndsAtMs) {
      this.tongueSweepEndsAtMs = 0;
      this.tongueDamagedThisSweep.clear();
      return;
    }
    if (!this.conservatoryCycle.isIncubation) return;

    const { minna, basil } = this.ctx;
    this.maybeDamageTongue("minna", minna.mesh.position.x);
    this.maybeDamageTongue("basil", basil.mesh.position.x);
  }

  private maybeDamageTongue(cat: "minna" | "basil", x: number): void {
    if (this.tongueDamagedThisSweep.has(cat)) return;
    if (x < TONGUE_SWEEP_FROM_X || x > TONGUE_SWEEP_TO_X) return;
    this.tongueDamagedThisSweep.add(cat);
    this.ctx.gameEvents.emit(GameEvents.PLAYER_DAMAGED, {
      cat,
      source: "sundewTongue",
      severity: "heavy",
    });
    const level = (this.ctx.scene.metadata as { level?: {
      respawn: (
        which: "minna" | "basil",
        target?: { x: number; y: number; z: number },
        reason?: "fall" | "fail" | "restore",
      ) => void;
    } }).level;
    // No new physics impulse — recovery is a position write via the shared
    // respawn funnel + checkpoint resolution (reason 'fail').
    level?.respawn(cat, undefined, "fail");
  }

  // ─────────────────────────────────────────────────────────────────
  // Phase 3 helpers
  // ─────────────────────────────────────────────────────────────────

  private isInDashZone(pos: Vector3): boolean {
    return isInsideSundewDashZone(pos);
  }

  private isInInternalChamber(pos: Vector3): boolean {
    return isInsideSundewInternalChamber(pos);
  }

  private resolveDashSync(): void {
    if (this.minnaEnteredMs === null || this.basilEnteredMs === null) return;

    const windowMs = this.chronosBubble.isActive
      ? DASH_WINDOW_MS_CHRONOS
      : DASH_WINDOW_MS_NORMAL;
    const delta = Math.abs(this.minnaEnteredMs - this.basilEnteredMs);

    if (delta <= windowMs) {
      // Dash-sync succeeded — both cats are now "inside" the maw. The apex
      // is NOT yet solved: canon requires Minna to hold her Purr from
      // within the internal chamber to unwire the Sundew (Phase 3B). Move
      // to `phase3b` so `tickPhase3b` can run the 6-second hold timer.
      this.enterPhase3b();
      return;
    }

    const clearMinna = this.minnaEnteredMs < this.basilEnteredMs;
    if (clearMinna) {
      this.minnaEnteredMs = null;
      this.minnaInZoneLast = false;
    } else {
      this.basilEnteredMs = null;
      this.basilInZoneLast = false;
    }

    if (this.elapsedMs - this.lastDesyncToastMs > 750) {
      this.lastDesyncToastMs = this.elapsedMs;
      this.ctx.gameEvents.emit(GameEvents.HUD_TOAST, {
        message: "Out of sync — try again.",
        color: COLORS.BIOLUMINESCENT_PURPLE,
      });
    }
  }

  // ─────────────────────────────────────────────────────────────────
  // Phase transitions
  // ─────────────────────────────────────────────────────────────────

  private enterPhase1(): void {
    if (this.phase !== "idle") return;
    this.phase = "phase1";
    this.heatPulseAccum = 0;
    this.ctx.gameEvents.emit(GameEvents.PUZZLE_STARTED, { puzzleId: this.id });
    this.ctx.gameEvents.emit(GameEvents.HUD_TOAST, {
      message: "Boiler-Bound Sundew: crank the three valves to vent the heat.",
      color: COLORS.BIOLUMINESCENT_GREEN,
    });
  }

  private enterPhase2(): void {
    if (this.phase !== "phase1") return;
    this.phase = "phase2";
    this.tongueSweepAccum = 0;
    this.tongueSweepEndsAtMs = 0;
    this.tongueDamagedThisSweep.clear();
    this.nodesRemaining = this.phase2Nodes.length;
    this.ctx.gameEvents.emit(GameEvents.SUNDEW_PHASE_ADVANCED, { phase: 2 });
    this.ctx.gameEvents.emit(GameEvents.HUD_TOAST, {
      message:
        "Corrupted nodes exposed. Aura Sight reveals them through the mist.",
      color: COLORS.BIOLUMINESCENT_BLUE,
    });
  }

  private enterPhase3(): void {
    if (this.phase !== "phase2") return;
    this.phase = "phase3";
    this.minnaEnteredMs = null;
    this.basilEnteredMs = null;
    this.minnaInZoneLast = false;
    this.basilInZoneLast = false;
    // Stage the canonical "iron jaws" framing — a tongue/maw-coloured
    // ring + particle breath that telegraphs the dash-zone as a live
    // predator mouth the cats must commit to. Book p.20-21 leans hard
    // on this imagery; SPEC §5.3 calls it out as a design gap.
    this.armIronJaws();
    this.ctx.gameEvents.emit(GameEvents.SUNDEW_PHASE_ADVANCED, { phase: 3 });
    this.ctx.gameEvents.emit(GameEvents.HUD_TOAST, {
      message:
        "Iron jaws gape open — dash in together before they snap shut!",
      color: COLORS.BIOLUMINESCENT_PURPLE,
    });
  }

  /**
   * Enter Phase 3B — Minna must hold her Purr (`F`) inside the internal
   * chamber for 6 seconds to unwire the Sundew from within.
   * Canon: book p.20-21 + SPEC §5.3 "unwiring from the inside."
   */
  private enterPhase3b(): void {
    if (this.phase !== "phase3") return;
    this.phase = "phase3b";
    this.internalPurrHeldMs = 0;
    this.internalPurrHeldLast = false;
    this.internalPurrLastEmitted = -1;
    // Jaws clamp shut behind the cats — lock the external dash framing
    // off so the HUD doesn't still tell the player to dash.
    this.disarmIronJaws();
    this.ctx.gameEvents.emit(GameEvents.SUNDEW_PHASE_ADVANCED, { phase: 3.5 });
    this.ctx.gameEvents.emit(GameEvents.HUD_TOAST, {
      message:
        "Inside the maw — Minna, hold the Purr to unwire it from within.",
      color: COLORS.BIOLUMINESCENT_GREEN,
    });
  }

  private completeApex(): void {
    if (this.phase === "solved") return;
    this.phase = "solved";
    this.disarmIronJaws();
    this.ctx.gameEvents.emit(GameEvents.SUNDEW_PHASE_ADVANCED, { phase: 4 });
    this.ctx.gameEvents.emit(GameEvents.PUZZLE_SOLVED, { puzzleId: this.id });
    this.ctx.gameEvents.emit(GameEvents.HUD_TOAST, {
      message: "Still point held. The Sundew releases the conservatory.",
      color: COLORS.EMERALD,
    });
  }

  // ─────────────────────────────────────────────────────────────────
  // Cross-phase input routing
  // ─────────────────────────────────────────────────────────────────

  private onTurnTableHit(): void {
    const { basil } = this.ctx;
    if (this.phase === "phase1") {
      const valve =
        this.pickSelectedValve(basil.mesh.position) ??
        this.pickNearestValve(basil.mesh.position);
      if (valve) valve.registerHit();
      return;
    }
    if (this.phase === "phase2") {
      const node =
        this.pickSelectedNode(basil.mesh.position) ??
        this.pickNearestNode(basil.mesh.position);
      if (node) node.registerHit();
      return;
    }
  }

  private pickSelectedValve(pos: Vector3): ValveCrank | null {
    if (!this.turnTableTargetKey) return null;
    const valve = this.phase1Valves.find(
      (candidate) =>
        candidate.key === this.turnTableTargetKey && !candidate.isActivated,
    );
    if (!valve) return null;
    return this.scoreTurnTableTarget(pos, valve.rootMesh.position) !== null
      ? valve
      : null;
  }

  private pickSelectedNode(pos: Vector3): CorruptedNode | null {
    if (!this.turnTableTargetKey) return null;
    const node = this.phase2Nodes.find(
      (candidate) =>
        candidate.key === this.turnTableTargetKey && !candidate.isCleared,
    );
    if (!node) return null;
    return this.scoreTurnTableTarget(pos, node.rootMesh.position) !== null
      ? node
      : null;
  }

  private pickNearestValve(pos: Vector3): ValveCrank | null {
    let best: ValveCrank | null = null;
    let bestScore = Infinity;
    for (const valve of this.phase1Valves) {
      if (valve.isActivated) continue;
      const score = this.scoreTurnTableTarget(pos, valve.rootMesh.position);
      if (score !== null && score < bestScore) {
        best = valve;
        bestScore = score;
      }
    }
    return best;
  }

  private pickNearestNode(pos: Vector3): CorruptedNode | null {
    let best: CorruptedNode | null = null;
    let bestScore = Infinity;
    for (const node of this.phase2Nodes) {
      if (node.isCleared) continue;
      const score = this.scoreTurnTableTarget(pos, node.rootMesh.position);
      if (score !== null && score < bestScore) {
        best = node;
        bestScore = score;
      }
    }
    return best;
  }

  private pickNearestTurnTableTarget(
    pos: Vector3,
  ): { key: string; position: Vector3 } | null {
    const target =
      this.phase === "phase1"
        ? this.pickNearestValve(pos)
        : this.phase === "phase2"
          ? this.pickNearestNode(pos)
          : null;
    if (!target) return null;
    return { key: target.key, position: target.rootMesh.position.clone() };
  }

  private scoreTurnTableTarget(pos: Vector3, target: Vector3): number | null {
    if (!isInsideSundewTurnTableReach(pos, target)) return null;
    const dx = Math.abs(pos.x - target.x);
    const dy = Math.abs(pos.y - target.y);
    return dx + dy * 0.12;
  }

  private updateTurnTableTarget(): void {
    if (!this.turnTableTargetKey) return;
    const current = this.pickNearestTurnTableTarget(this.ctx.basil.mesh.position);
    if (!current || current.key !== this.turnTableTargetKey) {
      this.deactivateTurnTableTarget();
    }
  }

  private deactivateTurnTableTarget(): void {
    if (!this.turnTableTargetKey) return;
    this.ctx.basilAbilities.deactivateAbility("turnTableTouch");
    this.turnTableTargetKey = null;
  }

  // ─────────────────────────────────────────────────────────────────
  // Iron-jaws dash framing (canon p.20-21)
  // ─────────────────────────────────────────────────────────────────

  /**
   * Stage the procedural "iron jaws" framing VFX around the dash zone.
   * Spawns a pulsing violet-red ring (tongue-maw silhouette) and a
   * slow-drifting particle breath so the player reads the risk before
   * committing. Performance-cheap: a single CPU particle system capped
   * at 60 particles and one torus mesh. Dispatches
   * `SUNDEW_IRON_JAWS_ARMED` so audio + screen-space shaders can
   * subscribe later without coupling into this class.
   */
  private armIronJaws(): void {
    if (this.ironJawsArmed) return;
    this.ironJawsArmed = true;

    const { scene } = this.ctx;

    const ring = MeshBuilder.CreateTorus(
      "sundew_ironJaws_ring",
      {
        diameter: DASH_ZONE_RADIUS * 2.2,
        thickness: 0.12,
        tessellation: 40,
      },
      scene,
    );
    ring.position = DASH_ZONE_CENTER.clone();
    // Vertical orientation so the torus reads as a maw-rim around the
    // side-scrolling dash axis, not a puddle on the floor.
    ring.rotation.x = Math.PI / 2;
    ring.isPickable = false;

    const ringMat = new StandardMaterial("sundew_ironJaws_ringMat", scene);
    ringMat.diffuseColor = Color3.FromHexString("#1a0303");
    ringMat.emissiveColor = Color3.FromHexString(
      COLORS.BIOLUMINESCENT_PURPLE,
    ).scale(0.55);
    ringMat.specularColor = Color3.Black();
    ringMat.alpha = 0.85;
    ring.material = ringMat;
    tagAs(ring, "placeholder");

    this.ironJawsRing = ring;
    this.ironJawsRingMat = ringMat;

    // Breath / spittle particle system pouring out of the maw. Keep the
    // emitter count small so this stays well under the per-scene particle
    // budget even on the lowest-tier machines.
    const particles = new ParticleSystem("sundew_ironJaws_breath", 60, scene);
    particles.emitter = DASH_ZONE_CENTER.clone();
    particles.minEmitBox = new Vector3(
      -DASH_ZONE_RADIUS * 0.4,
      -0.2,
      -DASH_ZONE_RADIUS * 0.4,
    );
    particles.maxEmitBox = new Vector3(
      DASH_ZONE_RADIUS * 0.4,
      0.2,
      DASH_ZONE_RADIUS * 0.4,
    );
    particles.minSize = 0.12;
    particles.maxSize = 0.32;
    particles.minLifeTime = 0.8;
    particles.maxLifeTime = 1.6;
    particles.emitRate = 28;
    particles.minEmitPower = 0.25;
    particles.maxEmitPower = 0.9;
    particles.direction1 = new Vector3(-0.4, 0.2, -1.2);
    particles.direction2 = new Vector3(0.4, 0.6, -0.4);
    particles.color1 = new Color4(0.62, 0.18, 0.55, 0.55);
    particles.color2 = new Color4(0.8, 0.3, 0.25, 0.3);
    particles.colorDead = new Color4(0.1, 0, 0.1, 0);
    particles.blendMode = ParticleSystem.BLENDMODE_ADD;
    try {
      // Procedural soft-radial sprite (same helper the Portal vortex
      // uses). The previous assets.babylonjs.com flare.png made this
      // effect depend on a third-party CDN at runtime — offline or
      // firewalled players got untextured squares.
      particles.particleTexture = createSoftParticleTexture(
        scene,
        "sundew_ironJaws_particleTexture",
      );
    } catch {
      // Non-fatal — Babylon will render flat particles without a texture.
    }
    try {
      particles.start();
    } catch {
      // NullEngine + headless runs may refuse to start a particle
      // system. Swallow so the core state machine is testable.
    }
    this.ironJawsParticles = particles;

    this.ctx.gameEvents.emit(GameEvents.SUNDEW_IRON_JAWS_ARMED, {
      armed: true,
      center: {
        x: DASH_ZONE_CENTER.x,
        y: DASH_ZONE_CENTER.y,
        z: DASH_ZONE_CENTER.z,
      },
      radius: DASH_ZONE_RADIUS,
    });
  }

  private disarmIronJaws(): void {
    const wasArmed = this.ironJawsArmed;
    this.ironJawsArmed = false;
    if (this.ironJawsParticles) {
      try {
        this.ironJawsParticles.stop();
      } catch {
        // ignore
      }
      this.ironJawsParticles.dispose();
      this.ironJawsParticles = null;
    }
    if (this.ironJawsRing) {
      this.ironJawsRing.dispose();
      this.ironJawsRing = null;
    }
    if (this.ironJawsRingMat) {
      this.ironJawsRingMat.dispose();
      this.ironJawsRingMat = null;
    }
    if (wasArmed) {
      this.ctx.gameEvents.emit(GameEvents.SUNDEW_IRON_JAWS_ARMED, {
        armed: false,
        center: {
          x: DASH_ZONE_CENTER.x,
          y: DASH_ZONE_CENTER.y,
          z: DASH_ZONE_CENTER.z,
        },
        radius: DASH_ZONE_RADIUS,
      });
    }
  }
}
